/* THERO About deck - six fixed frames on four sliding/morphing layers.
 *
 * How to use: sections/thero-about.liquid renders <thero-about-deck> with layers
 * marked [data-layer] and the HUD (navbg / navbar / mark). This controller owns
 * ALL motion: one gesture (wheel / swipe / key) advances exactly one frame and
 * plays that transition on a clock - never scroll-linked (per the motion spec,
 * scroll-linked choreography stutters and lets users park mid-cross-fade).
 *
 * Geometry + choreography numbers are design-locked from the THERO About motion
 * spec (Google Doc, extracted 2026-08-19) and are intentionally NOT merchant
 * settings - copy/images are editable in the customizer, timing is not.
 */
(function () {
  'use strict';

  /* ---------- design constants (390x844 artboard space) ---------- */

  var AW = 390;
  var AH = 844;
  var MID = 195.5; // the mark centres on Figma's 350px column, not on 390

  // Wordmark glyphs: T H E H E R O - indices 3,4 (the second H,E) collapse.
  var GLYPH = ['T', 'H', 'E', 'H', 'E', 'R', 'O'];
  var GLYPH_W = [39.172, 37.947, 34.611, 37.947, 34.611, 38.322, 45.641];
  var GX = [0, 44.467, 92.272, 151.68, 199.485, 240.48, 280.999]; // frame 1 (THE HERO)
  var GTO = [0.215, 43.572, 90.787, 126.883, 126.883, 131.36, 171.506]; // frame 2 (THERŌ)

  var MERGE_SCALE = 1.16470588;
  var NAV_SCALE = 0.45454545;
  var MERGE_Y0 = 293.08;
  var MERGE_Y1 = 268.25;
  var NAV_X = 59.8841;
  var NAV_Y = 55.6299;

  var INK = [21, 33, 42]; // #15212A
  var WHITE = [255, 255, 255];
  var FORMEN0 = [73, 79, 84]; // #494F54 - FOR MEN on the default (dark ink) wall; a custom Hero text colour replaces it

  // Short phones (stage shorter than the 844 design): keep the wall photo's composition by trimming up to
  // FLOOR_MAX design px of floor from the photo's bottom; only if that is not enough, lift the hero words by up
  // to LIFT_MAX (the kicker then still clears the nav bar). Measured 2026-09-10 on the live wall photo: the face
  // wash cap starts 49.7% down the photo and the shelf sits at 79%, so 150px of floor trims safely.
  var FLOOR_MAX = 150;
  var LIFT_MAX = 130;

  // Durations (ms)
  var D_MERGE = 1250;
  var D_SLIDE = 900;
  var D_SHELF = 1500;

  // Input thresholds (accumulated wheel px)
  var FIRST = 45;
  var AGAIN = 230;
  var IDLE = 320;

  // Shelf: 10 clutter items - o, dx, dy, rot, window start (end = start + 0.7)
  var CLUTTER = [
    { dx: -6.9, dy: 520, rot: -11, a: 0.0 },
    { dx: 96.5, dy: -560, rot: 11, a: 0.028 },
    { dx: 54.3, dy: 520, rot: -11, a: 0.056 },
    { dx: 39.7, dy: -560, rot: 11, a: 0.084 },
    { dx: 27.0, dy: 520, rot: -11, a: 0.112 },
    { dx: 9.7, dy: -560, rot: 11, a: 0.14 },
    { dx: -33.0, dy: 520, rot: -11, a: 0.168 },
    { dx: -54.0, dy: -560, rot: 11, a: 0.196 },
    { dx: -44.7, dy: 520, rot: -11, a: 0.224 },
    { dx: -16.9, dy: -560, rot: 11, a: 0.252 }
  ];

  /* ---------- easing ---------- */

  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function seg(p, a, b) { return clamp01((p - a) / (b - a)); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function outCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function outQuart(t) { return 1 - Math.pow(1 - t, 4); }
  function inOutCubic(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function mixRGB(c1, c2, t) {
    return 'rgb(' + Math.round(lerp(c1[0], c2[0], t)) + ',' + Math.round(lerp(c1[1], c2[1], t)) + ',' + Math.round(lerp(c1[2], c2[2], t)) + ')';
  }
  // Parse a #rgb / #rrggbb hex to [r,g,b]; null if not a valid hex. Used to feed
  // the customizer "Hero text colour" into the wordmark's wall-colour ramp.
  function hexToRgb(h) {
    if (!h) return null;
    h = ('' + h).trim().replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (h.length !== 6) return null;
    var n = parseInt(h, 16);
    if (isNaN(n)) return null;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- controller ---------- */

  var proto = Object.create(HTMLElement.prototype);

  proto.connectedCallback = function () {
    var el = this;
    // Computers with the desktop layout (html.thero-desktop, 990px+): the section shows its wide-screen
    // panels (.tadd) and this deck is hidden, so it must not start - its wheel / touch / key handlers would
    // otherwise block normal page scrolling. If the window later narrows to a phone width, start then.
    var deskMq = window.matchMedia('(min-width: 990px)');
    if (!el._deskWatch && document.documentElement.classList.contains('thero-desktop') && deskMq.matches) {
      el._deskWatch = function (e) {
        if (e.matches) return;
        deskMq.removeEventListener('change', el._deskWatch);
        proto.connectedCallback.call(el);
      };
      deskMq.addEventListener('change', el._deskWatch);
      return;
    }
    el.viewport = el.querySelector('[data-tad-viewport]');
    el.stage = el.querySelector('[data-tad-stage]');
    el.layers = Array.prototype.slice.call(el.querySelectorAll('[data-tad-layer]'));
    el.hud = el.querySelector('[data-tad-hud]');
    el.navbg = el.querySelector('[data-tad-navbg]');
    el.navbar = el.querySelector('[data-tad-navbar]');
    el.mark = el.querySelector('[data-tad-mark]');
    // Customizer hero text colour: the wordmark starts this colour on the wall
    // and still ramps to WHITE as it flies into the navbar. Falls back to INK.
    el._heroInk = hexToRgb(getComputedStyle(el).getPropertyValue('--tad-hero-ink')) || INK;
    // FOR MEN starts in the same custom colour as the letters (was always #494F54 - invisible on a dark wall)
    el._formenInk = el.hasAttribute('data-hero-tinted') ? el._heroInk : FORMEN0;
    el.glyphEls = Array.prototype.slice.call(el.querySelectorAll('[data-tad-glyph]'));
    el.macron = el.querySelector('[data-tad-macron]');
    el.formen = el.querySelector('[data-tad-formen]');
    el.live = el.querySelector('[data-tad-live]');
    el.scrollCue = el.querySelector('[data-tad-scrollcue]');
    el.ambientA = el.querySelector('[data-tad-ambient="0"]');
    el.ambientB = el.querySelector('[data-tad-ambient="1"]');

    // Frame list is built from the layers actually present (a hidden customizer
    // block removes its layer, and the deck adapts).
    el.frames = [];
    el.layers.forEach(function (layer, li) {
      var kind = layer.getAttribute('data-tad-layer');
      if (kind === 'hero') {
        el.frames.push({ layer: li, phase: 0, announce: layer.getAttribute('data-announce-1') || 'The hero' });
        el.frames.push({ layer: li, phase: 1, announce: layer.getAttribute('data-announce-2') || 'THERO for men' });
      } else if (kind === 'shelf') {
        el.frames.push({ layer: li, phase: 0, announce: layer.getAttribute('data-announce-1') || 'The shelf' });
        el.frames.push({ layer: li, phase: 1, announce: layer.getAttribute('data-announce-2') || 'But THERO is' });
      } else {
        el.frames.push({ layer: li, phase: 0, announce: layer.getAttribute('data-announce-1') || '' });
      }
      layer._kind = kind;
    });
    if (!el.frames.length) return;

    el.hasHero = el.layers.length && el.layers[0]._kind === 'hero';

    // Value state: mark merge (0..1), mark flight to nav (0..1)
    el.V = { mark: el.hasHero ? 0 : 1, fly: el.hasHero ? 0 : 1, shelf: 0 };
    el.idx = 0;
    el.busy = false;

    // Mobile: fill the WIDTH edge-to-edge (scale by width, stage height adapts -
    // scenes anchor top/bottom in CSS so nothing is cropped). Desktop/tablet:
    // present the reference artboard (contain fit) over the ambient scene fill.
    el.fit = function () {
      var vw = window.innerWidth;
      var vh = window.innerHeight;
      var s;
      var h;
      if (vw < 750) {
        s = vw / AW;
        h = Math.ceil(vh / s);
      } else {
        s = Math.min(vw / AW, vh / AH);
        h = AH;
      }
      el.scale = s;
      el.stageH = h;
      el.stage.style.width = AW + 'px';
      el.stage.style.height = h + 'px';
      el.stage.style.transform = 'scale(' + s + ')';
      // Hero wall on a short phone: the photo is bottom-anchored while the words are top-anchored, so a stage
      // shorter than 844 slid the products up under the words. Trim floor first (--tad-floor makes the photo
      // taller than the stage; the stage clips it), then lift the words (--tad-lift, also read by renderMark).
      var deficit = Math.max(0, AH - h);
      var fitOn = !el.hasAttribute('data-hero-nofit');
      var floor = fitOn ? Math.min(deficit, FLOOR_MAX) : 0;
      var lift = fitOn ? Math.min(deficit - floor, LIFT_MAX) : 0;
      el.style.setProperty('--tad-floor', String(floor));
      el.style.setProperty('--tad-lift', String(lift));
      el._lift = lift;
      if (el.mark) el.renderMark();
      // parked layers sit exactly one stage-height away; re-park on resize
      el.layers.forEach(function (layer) {
        if (layer._y) layer._y = (layer._y < 0 ? -1 : 1) * h;
        layer.style.transform = 'translate3d(0,' + (layer._y || 0) + 'px,0)';
      });
    };
    el.fit();
    window.addEventListener('resize', el.fit);
    window.addEventListener('orientationchange', el.fit);

    el.renderAll();
    el.bindInput();
    el.bindHudActions();
    el.setActive(0, { initial: true, reveal: true });
  };

  /* ---------- rendering ---------- */

  proto.renderAll = function () {
    this.renderLayers();
    this.renderMark();
    this.renderNav();
    this.renderShelf(this.V.shelf);
  };

  // Layer parking: active at 0, passed at -844, ahead at +844 (translated in
  // stage px; the stage itself is scaled).
  proto.renderLayers = function () {
    var el = this;
    var activeLayer = el.frames[el.idx].layer;
    el.layers.forEach(function (layer, li) {
      if (layer._y === undefined) layer._y = li === activeLayer ? 0 : li < activeLayer ? -(el.stageH || AH) : (el.stageH || AH);
      layer.style.transform = 'translate3d(0,' + layer._y + 'px,0)';
      var offstage = li !== activeLayer && Math.abs(layer._y) >= (el.stageH || AH) - 0.5;
      layer.toggleAttribute('inert', offstage);
      layer.style.visibility = offstage && !el.busy ? 'hidden' : 'visible';
    });
  };

  // The wordmark: seven glyphs, one container, driven by V.mark (merge) and
  // V.fly (flight into the nav bar).
  proto.renderMark = function () {
    var el = this;
    if (!el.mark) return;
    var m = el.V.mark;
    var f = el.V.fly;
    var e = inOutCubic(m);
    var k = outQuart(seg(m, 0, 0.7));
    var F = outCubic(f);

    var scale = lerp(1, MERGE_SCALE, e);
    // on the wall the mark rides with the hero words (lifted on short phones - see el.fit); the nav target is fixed
    var y = lerp(MERGE_Y0, MERGE_Y1, e) - (el._lift || 0);

    // container x keeps the optical centre pinned while the row narrows
    var rowLeft = lerp(GX[0], GTO[0], k);
    var rowRight = lerp(GX[6], GTO[6], k) + GLYPH_W[6];
    var rowWidth = rowRight - rowLeft;
    var x = MID - (rowWidth * scale) / 2 - rowLeft * scale;

    if (f > 0) {
      x = lerp(x, NAV_X, F);
      y = lerp(y, NAV_Y, F);
      scale = lerp(scale, NAV_SCALE, F);
    }

    el.mark.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) scale(' + scale + ')';

    // glyph positions + the collapsing pair (implodes about its own centre)
    el.glyphEls.forEach(function (g, i) {
      // element origin is the glyph's own left edge (viewBox-cropped SVG), so
      // translate by (target - original) glyph x
      var gx = lerp(GX[i], GTO[i], k) - GX[i];
      var t = 'translate3d(' + gx + 'px,0,0)';
      if (i === 3 || i === 4) {
        t += ' scaleX(' + (1 - k) + ')';
        g.style.opacity = 1 - seg(k, 0, 0.55);
      }
      g.style.transform = t;
    });

    // macron over the Ō + FOR MEN
    var mac = outCubic(seg(m, 0.5, 1));
    if (el.macron) {
      el.macron.style.opacity = mac;
      el.macron.style.transform = 'translate3d(' + lerp(GX[6], GTO[6], k) + 'px,0,0) scaleX(' + mac + ')';
    }
    if (el.formen) el.formen.style.opacity = outCubic(seg(m, 0.55, 1));

    // colour ramp: ink gradient on the wall -> solid white in the bar
    // (glyph gradient stops are currentColor; the tail stop's opacity rides --mkg2)
    var cw = outCubic(seg(f, 0.05, 0.6));
    el.mark.style.color = mixRGB(el._heroInk || INK, WHITE, cw);
    el.mark.style.setProperty('--mkg2', String(lerp(0.55, 1, f)));
    if (el.formen) el.formen.style.color = mixRGB(el._formenInk || FORMEN0, WHITE, cw);

    // hero-frame copy fades as the merge runs (kicker/rule/subline + SCROLL cue)
    var hero = el.layers[0];
    if (hero && hero._kind === 'hero') {
      var out = seg(m, 0, 0.34);
      hero.style.setProperty('--m-out', out);
      hero.style.setProperty('--m-photo', e);
      if (el.scrollCue) el.scrollCue.style.opacity = String((1 - seg(m, 0, 0.18)) * (1 - seg(el._moved || 0, 0, 1)));
    }
  };

  // Nav bar background + icon colour follow the flight clock. The logo-box
  // home link only exists while the mark is actually parked in the bar.
  proto.renderNav = function () {
    var el = this;
    var f = el.V.fly;
    var w = outCubic(seg(f, 0.05, 0.6));
    if (el.navbg) el.navbg.style.opacity = w;
    // wall nav icons start at the hero text colour (so they stay legible on a dark
    // photo when the copy is light) and ramp to white as the slate bar fills in
    if (el.navbar) el.navbar.style.color = mixRGB(el._heroInk || INK, WHITE, w);
    el.toggleAttribute('data-mark-innav', f > 0.98);
  };

  // The shelf morph (frames 4 -> 5) driven by p 0..1.
  proto.renderShelf = function (p) {
    var el = this;
    var layer = null;
    for (var i = 0; i < el.layers.length; i++) if (el.layers[i]._kind === 'shelf') layer = el.layers[i];
    if (!layer) return;

    // clutter scatter
    var bits = layer.querySelectorAll('[data-tad-clutter]');
    Array.prototype.forEach.call(bits, function (b, o) {
      var c = CLUTTER[o % CLUTTER.length];
      var t = inOutCubic(seg(p, c.a, 0.7 + c.a));
      b.style.transform = 'translate3d(' + c.dx * t + 'px,' + c.dy * t + 'px,0) rotate(' + c.rot * t + 'deg) scale(' + lerp(1, 0.9, t) + ')';
      b.style.opacity = 1 - seg(t, 0.5, 1) * 0.55;
    });

    // THERO products emerge into the light
    var prods = layer.querySelectorAll('[data-tad-product]');
    Array.prototype.forEach.call(prods, function (pr, j) {
      var tp = outCubic(seg(p, 0.22 + j * 0.05, 0.8 + j * 0.05));
      pr.style.transform = 'translate3d(0,' + lerp(12, 0, tp) + 'px,0) scale(' + lerp(0.985, 1, tp) + ')';
      pr.style.filter = 'drop-shadow(0 10px 20px rgba(30,36,42,' + lerp(0.04, 0.16, tp) + ')) brightness(' + lerp(0.94, 1, tp) + ')';
    });

    // headline swap
    var h = inOutCubic(seg(p, 0.24, 0.66));
    layer.style.setProperty('--sh-h', h);

    // complaints out, steps in
    var q = inOutCubic(seg(p, 0.18, 0.58));
    var r = outCubic(seg(p, 0.42, 1));
    layer.style.setProperty('--sh-q', q);
    layer.style.setProperty('--sh-r', r);
    var cards = layer.querySelectorAll('[data-tad-step]');
    Array.prototype.forEach.call(cards, function (cardEl, kk) {
      var t2 = outCubic(seg(p, 0.44 + kk * 0.05, 0.94 + kk * 0.05));
      cardEl.style.opacity = t2;
      cardEl.style.transform = 'translate3d(0,' + lerp(26, 0, t2) + 'px,0) scale(' + lerp(0.96, 1, t2) + ')';
    });
  };

  /* ---------- transitions ---------- */

  proto.setActive = function (idx, opts) {
    var el = this;
    opts = opts || {};
    el.idx = idx;
    var frame = el.frames[idx];
    el.layers.forEach(function (layer, li) {
      layer.classList.toggle('is-active', li === frame.layer);
      // per-phase attribute lets CSS pick which morph state the layer shows
      if (li === frame.layer) layer.setAttribute('data-phase', frame.phase);
    });
    // arrival reveals fire ONCE per arrival (at transition start) - the done
    // callback re-calls setActive with reveal:false so the stagger never
    // restarts mid-flight, and morph-reached frames never re-fire it.
    var layer = el.layers[frame.layer];
    if (opts.reveal && layer && frame.phase === 0 && layer._kind !== 'hero') {
      layer.classList.remove('in');
      void layer.offsetWidth;
      layer.classList.add('in');
    }
    if (opts.initial && layer && layer._kind === 'hero') layer.classList.add('in');
    if (el.live && frame.announce) el.live.textContent = frame.announce;
    el.renderAmbient(frame.layer);
    el.renderLayers();
  };

  // Ambient viewport fill: crossfade to the active scene's background (blurred
  // cover behind the artboard) so off-artboard space is never a flat letterbox.
  proto.renderAmbient = function (layerIdx) {
    var el = this;
    if (!el.ambientA || !el.ambientB) return;
    var layer = el.layers[layerIdx];
    if (!layer) return;
    var img = layer.getAttribute('data-ambient-img');
    var color = layer.getAttribute('data-ambient-color') || '';
    var key = img || color;
    if (key === el._ambientKey) return;
    el._ambientKey = key;
    el._ambientFlip = !el._ambientFlip;
    var show = el._ambientFlip ? el.ambientA : el.ambientB;
    var hide = el._ambientFlip ? el.ambientB : el.ambientA;
    if (img) {
      show.style.backgroundImage = 'url("' + img + '")';
      show.style.backgroundColor = '';
    } else {
      show.style.backgroundImage = 'none';
      show.style.backgroundColor = color;
    }
    show.classList.add('is-on');
    hide.classList.remove('is-on');
  };

  proto.animate = function (dur, step, done) {
    var el = this;
    if (REDUCED) dur = 1;
    el.busy = true;
    var t0 = performance.now();
    function tick(now) {
      var p = clamp01((now - t0) / dur);
      step(p);
      if (p < 1) { el._raf = requestAnimationFrame(tick); } else { el.busy = false; done && done(); }
    }
    el._raf = requestAnimationFrame(tick);
  };

  proto.go = function (dir) {
    var el = this;
    if (el.busy) return;
    var from = el.idx;
    var to = from + dir;
    if (to < 0 || to >= el.frames.length) return;
    el._moved = 1;

    var A = el.frames[from];
    var B = el.frames[to];
    var sameLayer = A.layer === B.layer;
    var layerA = el.layers[A.layer];
    var layerB = el.layers[B.layer];

    if (sameLayer && layerA._kind === 'hero') {
      // frame 1 <-> 2 : the wordmark merge (in place)
      el.animate(D_MERGE, function (p) {
        el.V.mark = dir > 0 ? p : 1 - p;
        el.renderMark();
      }, function () { el.V.mark = dir > 0 ? 1 : 0; el.renderMark(); el.setActive(to, { reveal: false }); });
      el.setActive(to, { reveal: false });
      return;
    }

    if (sameLayer && layerA._kind === 'shelf') {
      // frame 4 <-> 5 : the shelf clears (in place)
      el.animate(D_SHELF, function (p) {
        el.V.shelf = dir > 0 ? p : 1 - p;
        el.renderShelf(el.V.shelf);
      }, function () { el.V.shelf = dir > 0 ? 1 : 0; el.renderShelf(el.V.shelf); el.setActive(to, { reveal: false }); });
      el.setActive(to, { reveal: false });
      return;
    }

    // layer slide - and if we're leaving (or re-entering) the hero layer the
    // mark flies on the SAME clock (one rAF loop drives both; per the spec a
    // CSS transition + JS tween combo drifts).
    var leavingHero = dir > 0 && layerA._kind === 'hero';
    var returningHero = dir < 0 && layerB._kind === 'hero';
    if (leavingHero && el.V.mark < 1) { el.V.mark = 1; el.renderMark(); } // force merge complete
    var yA0 = layerA._y || 0;
    var SH = el.stageH || AH;
    var yB0 = layerB._y !== undefined ? layerB._y : dir > 0 ? SH : -SH;
    layerB._y = yB0;
    layerB.style.visibility = 'visible';
    layerB.removeAttribute('inert');
    // pre-position phase state of target layer
    layerB.setAttribute('data-phase', B.phase);
    if (layerB._kind === 'shelf') { el.V.shelf = B.phase === 1 ? 1 : 0; el.renderShelf(el.V.shelf); }

    el.animate(D_SLIDE, function (p) {
      var t = outCubic(p);
      layerA._y = lerp(yA0, dir > 0 ? -SH : SH, t);
      layerB._y = lerp(yB0, 0, t);
      layerA.style.transform = 'translate3d(0,' + layerA._y + 'px,0)';
      layerB.style.transform = 'translate3d(0,' + layerB._y + 'px,0)';
      if (leavingHero) { el.V.fly = p; el.renderMark(); el.renderNav(); }
      if (returningHero) { el.V.fly = 1 - p; el.renderMark(); el.renderNav(); }
    }, function () {
      layerA._y = dir > 0 ? -SH : SH;
      layerB._y = 0;
      if (leavingHero) el.V.fly = 1;
      if (returningHero) el.V.fly = 0;
      el.renderMark();
      el.renderNav();
      el.setActive(to, { reveal: false });
    });
    el.setActive(to, { reveal: true });
  };

  proto.goTo = function (target) {
    // Home/End walk one frame at a time so the in-place morphs still play.
    var el = this;
    if (el.busy || target === el.idx) return;
    var dir = target > el.idx ? 1 : -1;
    var stepNext = function () {
      if (el.idx !== target && !el.busy) {
        el.go(dir);
        setTimeout(stepNext, REDUCED ? 10 : 60);
      } else if (el.busy) {
        setTimeout(stepNext, 60);
      }
    };
    stepNext();
  };

  /* ---------- input ---------- */

  proto.bindInput = function () {
    var el = this;
    var acc = 0;
    var gestureOpen = false;
    var quietTimer = null;

    // While a drawer/dialog is open over the deck (cart, menu, search), the
    // deck must NOT swallow input - those surfaces scroll internally.
    function overlayOpen() {
      // Also stand down while the deck itself is hidden (a window widened to a computer size shows the
      // .tadd panels instead) - the page must scroll normally then.
      if (el.getClientRects().length === 0) return true;
      return !!document.querySelector('dialog[open], .thero-drawer__overlay[open]') ||
        document.documentElement.classList.contains('thero-drawer-open');
    }
    el._overlayOpen = overlayOpen;

    function feed(delta) {
      if (el.busy) return;
      acc += delta;
      clearTimeout(quietTimer);
      quietTimer = setTimeout(function () { acc = 0; gestureOpen = false; }, IDLE);
      var need = gestureOpen ? AGAIN : FIRST;
      if (Math.abs(acc) >= need) {
        var dir = acc > 0 ? 1 : -1;
        acc = 0;
        gestureOpen = true;
        el.go(dir);
      }
    }

    el._onWheel = function (e) { if (overlayOpen()) return; e.preventDefault(); feed(e.deltaY); };
    document.addEventListener('wheel', el._onWheel, { passive: false });

    var lastY = null;
    el._onTouchStart = function (e) { lastY = e.touches[0].clientY; acc = 0; };
    el._onTouchMove = function (e) {
      if (overlayOpen()) return;
      e.preventDefault();
      if (lastY === null) { lastY = e.touches[0].clientY; return; }
      var y = e.touches[0].clientY;
      feed((lastY - y) * 2.2);
      lastY = y;
    };
    el._onTouchEnd = function () { lastY = null; acc = 0; };
    document.addEventListener('touchstart', el._onTouchStart, { passive: true });
    document.addEventListener('touchmove', el._onTouchMove, { passive: false });
    document.addEventListener('touchend', el._onTouchEnd, { passive: true });

    el._onKey = function (e) {
      if (e.defaultPrevented || overlayOpen()) return;
      switch (e.key) {
        case 'ArrowDown': case 'PageDown': case ' ': case 'Enter':
          if (e.target && /^(input|textarea|select|button|a)$/i.test(e.target.tagName)) return;
          e.preventDefault(); el.go(1); break;
        case 'ArrowUp': case 'PageUp':
          e.preventDefault(); el.go(-1); break;
        case 'Home': e.preventDefault(); el.goTo(0); break;
        case 'End': e.preventDefault(); el.goTo(el.frames.length - 1); break;
      }
    };
    document.addEventListener('keydown', el._onKey);
  };

  proto.bindHudActions = function () {
    var el = this;
    var burger = el.querySelector('[data-tad-menu]');
    if (burger) {
      burger.addEventListener('click', function () {
        var drawer = document.querySelector('thero-drawer');
        if (drawer && drawer.open) drawer.open();
        else { var b = document.querySelector('#header-group [data-thero-drawer-open]'); if (b) b.click(); }
      });
    }
    var search = el.querySelector('[data-tad-search]');
    if (search) {
      search.addEventListener('click', function () {
        var modal = document.getElementById('search-modal');
        if (modal && modal.showDialog) modal.showDialog();
        else window.location.href = '/search';
      });
    }
    // bag button carries [data-thero-cart-open] in the markup - thero-cart.js's
    // document-level delegate opens the drawer; nothing to do here.
  };

  proto.disconnectedCallback = function () {
    var el = this;
    window.removeEventListener('resize', el.fit);
    window.removeEventListener('orientationchange', el.fit);
    document.removeEventListener('wheel', el._onWheel);
    document.removeEventListener('touchstart', el._onTouchStart);
    document.removeEventListener('touchmove', el._onTouchMove);
    document.removeEventListener('touchend', el._onTouchEnd);
    document.removeEventListener('keydown', el._onKey);
    if (el._raf) cancelAnimationFrame(el._raf);
  };

  function TheroAboutDeck() { return Reflect.construct(HTMLElement, [], TheroAboutDeck); }
  TheroAboutDeck.prototype = proto;
  Object.setPrototypeOf(TheroAboutDeck, HTMLElement);
  if (!customElements.get('thero-about-deck')) customElements.define('thero-about-deck', TheroAboutDeck);
})();
